import { supabase } from './supabase';
import { logEvent, newTraceId, setSupabaseClient } from '../../lib/obs/log.mjs';

// Inject the browser supabase singleton into the spine helper. observability.js
// does this too, but error reporting must not DEPEND on observability.js having
// been imported first — if it hasn't, logEvent would have no client and would
// drop the row. Same singleton, so the duplicate call is a harmless no-op.
setSupabaseClient(supabase);

let sessionId = null;

/** Expose session ID for cross-module correlation (used by observability.js). */
export function getSessionId() {
  return sessionId;
}

// Lazy trace-context access to avoid a circular import with observability.js
// (observability.js imports getSessionId from this file).
async function _safeTraceContext() {
  try {
    const mod = await import('./observability');
    return typeof mod.getTraceContext === 'function' ? mod.getTraceContext() : {};
  } catch {
    return {};
  }
}

export function initErrorReporter() {
  sessionId = crypto.randomUUID?.() || Date.now().toString(36);

  window.addEventListener('error', async (e) => {
    const ctx = await _safeTraceContext();
    logAppError({
      type: 'ui_runtime_error',
      message: e.message,
      stack: e.error?.stack,
      metadata: { phase_tag: 'phase_a2', caught_by: 'window.error', ...ctx },
    });
  });

  window.addEventListener('unhandledrejection', async (e) => {
    const msg = e.reason?.message || String(e.reason);
    const ctx = await _safeTraceContext();
    logAppError({
      type: 'ui_runtime_error',
      message: msg,
      stack: e.reason?.stack,
      // TICKET-1100: DOMExceptions (e.g. WebKit IDB 'UnknownError') carry no stack —
      // name + UA are the only way to tell which device/engine failed.
      metadata: { phase_tag: 'phase_a2', caught_by: 'window.unhandledrejection', error_name: e.reason?.name, ua: navigator.userAgent, last_idb_op: globalThis.__acnLastIdbOp || null, ...ctx },
    });
  });
}

const SLOW_THRESHOLD_MS = 2000; // 2s — any single fetch over this is a problem

/**
 * Wrap an async function with timing — logs slow_query if it exceeds threshold.
 * @param {string} name - Query/operation name for logging
 * @param {Function} fn - Async function to time
 * @returns {Promise<*>} Result of fn()
 */
export async function timedFetch(name, fn) {
  const start = performance.now();
  try {
    const result = await fn();
    const duration = Math.round(performance.now() - start);
    if (duration > SLOW_THRESHOLD_MS) {
      logAppError({
        type: 'slow_query',
        message: `${name} took ${duration}ms`,
        metadata: { queryName: name, duration_ms: duration },
      });
    }
    return result;
  } catch (err) {
    const duration = Math.round(performance.now() - start);
    logAppError({
      type: 'api_error',
      message: `${name} failed after ${duration}ms: ${err.message}`,
      metadata: { queryName: name, duration_ms: duration },
    });
    throw err;
  }
}

export async function logAppError({ type = 'js_error', message, stack, view, metadata = {} }) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    // FQ-57: canonical emitter (was a raw app_error_logs insert). Row shape is
    // unchanged — logEvent passes error_type/message/stack/metadata/app_version
    // through verbatim — but the canonical columns are populated too.
    //
    // Deliberately NOT routed via observability.js `_log()`: that path applies
    // the TICKET-325 ceremony sampling gate, and runtime errors are the single
    // most important thing in the spine. They emit at 100%, always.
    //
    // No circular import: this file imports lib/obs/log.mjs directly, which
    // imports nothing from src/. (observability.js imports getSessionId from
    // HERE, which is why the trace-context read above is lazy.)
    await logEvent({
      trace_id: newTraceId(),
      source: 'client',
      module: 'ui',
      step: type,
      status: 'error',
      page: typeof window !== 'undefined' ? window.location.pathname : null,
      session_id: sessionId,
      user_id: user?.id || null,
      view_name: view || metadata.view || null,
      error_type: type,
      message: (message || '').slice(0, 2000),
      stack: (stack || '').slice(0, 4000),
      metadata: { ...metadata, sessionId },
      app_version: import.meta.env.VITE_APP_VERSION || null,
    });
  } catch {
    // Fire-and-forget — never block UI
  }
}
